import { RequisitionStatus } from "../../../generated/graphql";
import { RequisitionStatusCancelledIcon } from "../icons/RequisitionStatusCancelledIcon";
import { RequisitionStatusApprovedIcon } from "../icons/RequisitionStatusApprovedIcon";
import { RequisitionStatusDraftIcon } from "../icons/RequisitionStatusDraftIcon";
import { RequisitionStatusRejectedIcon } from "../icons/RequisitionStatusRejectedIcon";
import { RequisitionStatusPendingIcon } from "../icons/RequisitionStatusPendingIcon";
import { orderBy } from "lodash";
import { RequisitionStatusClosedIcon } from "../icons/RequisitionStatusClosedIcon";
import type { MessageDescriptor } from "react-intl";
import { RequisitionStatusGenericIcon } from "../icons/RequisitionStatusGenericIcon";

type TranslateStatusMessage = (message: MessageDescriptor) => string;

export const requisitionStatuses = Object.values(RequisitionStatus);

export const statusLabelByStatus: Record<RequisitionStatus, string> = {
  DRAFT: "requisitionstatus-draft",
  PENDING_APPROVAL: "requisitionstatus-pendingApproval",
  APPROVED: "requisitionstatus-approved",
  CLOSED: "requisitionstatus-close",
  REJECTED: "requisitionstatus-rejected",
  CANCELLED: "requisitionstatus-cancelled",
};

export const getRequisitionStatusTranslationId = (status?: string | null) =>
  status ? statusLabelByStatus[status as RequisitionStatus] : undefined;

export const getRequisitionStatusLabel = (status?: string | null) =>
  getRequisitionStatusTranslationId(status) ?? status ?? "";

export const getRequisitionStatusDisplayName = (
  status?: string | null,
  fallbackName?: string,
  translate?: TranslateStatusMessage,
) => {
  const translationId = getRequisitionStatusTranslationId(status);

  if (translationId && translate) {
    return translate(
      fallbackName
        ? { id: translationId, defaultMessage: fallbackName }
        : { id: translationId },
    );
  }

  return fallbackName ?? status ?? "";
};

export const getRequisitionStatusIcon = (status: string) => {
  switch (status) {
    case RequisitionStatus.Approved:
      return <RequisitionStatusApprovedIcon />;
    case RequisitionStatus.Draft:
      return <RequisitionStatusDraftIcon />;
    case RequisitionStatus.PendingApproval:
      return <RequisitionStatusPendingIcon />;
    case RequisitionStatus.Rejected:
      return <RequisitionStatusRejectedIcon />;
    case RequisitionStatus.Cancelled:
      return <RequisitionStatusCancelledIcon />;
    case RequisitionStatus.Closed:
      return <RequisitionStatusClosedIcon />;
    default:
      return <RequisitionStatusGenericIcon />;
  }
};

export const requisitionStatusSelectEntities = orderBy(
  requisitionStatuses.map((status) => ({
    id: status,
    name: getRequisitionStatusLabel(status),
  })),
  ({ id }) => Object.keys(statusLabelByStatus).indexOf(id),
);
